import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { MediaQueryWrapper } from '@teliaee/telia-common-components';
import { CollapseProvider, EqualHeightProvider, Separator, ThemeContext } from 'telia-front-react';
import InternetCalculatorStore from '../../stores/InternetCalculatorStore';
import { ProductCard } from './InternetCalculatorInterfaces';
import InternetCardsGrid from './InternetCardsGrid';
import InternetCardsMobileGrid from './InternetCardsMobileGrid';
import { getInternetTech } from './InternetSpeedUtil';
import InternetCalculatorNotices from './notice/InternetCalculatorNotices';
import InternetOversoldWarning from './notice/InternetOversoldWarning';
import SinglePlannedNetworkCardHeading from './notice/SinglePlannedNetworkCardHeading';
import RequestFasterInternet from './RequestFasterInternet';

interface InternetSpeedSelectionProps {
  calculatorKey: string;
  partner: boolean;
  notices?: React.ReactNode;
  internetCalculatorStore?: InternetCalculatorStore;
}

const InternetSpeedSelection = (props: InternetSpeedSelectionProps) => {
  React.useContext(ThemeContext);

  const getCurrentInternetCards = (): ProductCard[] => {
    const { calculatorKey, internetCalculatorStore, partner } = props;
    return partner ? internetCalculatorStore.partnerProducts(calculatorKey) : internetCalculatorStore.teliaProducts(calculatorKey);
  };

  const isExcludedFromRecommendations = (offeringCode: string): boolean => {
    const { excludedWhen5G, internetCodes5G } = internetCalculatorStore.getState(calculatorKey).propState;
    const currentInternetCards = getCurrentInternetCards();
    const has5G = internetCodes5G?.some((code) => currentInternetCards.map((internet) => internet.productOfferingCode).includes(code));
    return has5G && excludedWhen5G?.includes(offeringCode);
  };

  const isRecommendedInternet = (offeringCode: string): boolean => {
    const { calculatorKey, internetCalculatorStore } = props;
    const { promotedInternets } = internetCalculatorStore.getState(calculatorKey).propState;
    const recommendBasedOnSuggestions = !!internetCalculatorStore.getState(calculatorKey).suggestedInternets;
    const recommendedInternets = internetCalculatorStore.getState(calculatorKey).suggestedInternets || promotedInternets || [];
    const currentInternetCards = getCurrentInternetCards();
    const matchingRecommendations = recommendedInternets.filter((recommendation) =>
      currentInternetCards.find((offer) => offer.productOfferingCode === recommendation && !offer.liableOnlyForSalesConsultant),
    );
    const firstMatch = matchingRecommendations.find((internet) => recommendBasedOnSuggestions || !isExcludedFromRecommendations(internet));
    if (firstMatch) {
      return offeringCode === firstMatch;
    }
    return currentInternetCards?.find((card) => !card.liableOnlyForSalesConsultant)?.productOfferingCode === offeringCode;
  };

  const { internetCalculatorStore, calculatorKey, partner } = props;
  const calculatorState = internetCalculatorStore.getState(calculatorKey);
  const { promotedInternets } = internetCalculatorStore.getState(calculatorKey).propState;
  const recommendedInternets = internetCalculatorStore.getState(calculatorKey).suggestedInternets || promotedInternets;
  const hasProducts = partner ? internetCalculatorStore.hasPartnerProducts(calculatorKey) : internetCalculatorStore.hasTeliaProducts(calculatorKey);

  if (!hasProducts) {
    if (partner) {
      return null;
    } else {
      return (
        <RequestFasterInternet
          partner={false}
          calculatorKey={calculatorKey}
          internetCalculatorStore={internetCalculatorStore}
          renderInsteadOfCardSelection={true}
        />
      );
    }
  }

  const cardTechs = internetCalculatorStore
    .getTeliaProductsWithoutPlannedNetwork(calculatorKey)
    .concat(internetCalculatorStore.getPartnerProductsWithoutPlannedNetwork(calculatorKey))
    .map((card) => getInternetTech(card.name, card.fix4g));

  return (
    <>
      <InternetOversoldWarning calculatorKey={calculatorKey} partner={partner} />
      <CollapseProvider id="voteForFasterInternet">
        <SinglePlannedNetworkCardHeading calculatorKey={calculatorKey} partner={partner} />
        <EqualHeightProvider groups={['internet-speed', 'card-text', 'price']}>
          <MediaQueryWrapper
            minWidth="lg"
            unmatchingContent={<InternetCardsMobileGrid calculatorKey={calculatorKey} partner={partner} isRecommendedInternet={isRecommendedInternet} />}
            matchingContent={
              <InternetCardsGrid
                calculatorKey={calculatorKey}
                partner={partner}
                recommendedInternets={recommendedInternets}
                isRecommendedInternet={isRecommendedInternet}
              />
            }
          />
        </EqualHeightProvider>
        <RequestFasterInternet partner={partner} calculatorKey={calculatorKey} />
      </CollapseProvider>
      <Separator spacing="xsmall" color="invisible" />
      <InternetCalculatorNotices
        notices={props.notices}
        partner={partner}
        segment={calculatorState.propState.segment}
        internetTechsOffered={new Set(cardTechs)}
      />
    </>
  );
};

export default inject('internetCalculatorStore')(observer(InternetSpeedSelection));
